import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Download, FileText, X, CheckCircle, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import apiClient from "../../utils/apiClient";
import Papa from "papaparse";
import server from "../../utils/serverConfig";

const FyersOrderPlacement = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [orderResults, setOrderResults] = useState(null);

  // Handle CSV file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Please upload a CSV file");
        return;
      }
      setCsvFile(file);
      parseCSV(file);
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          setParsedData(results.data);
          setPreviewData(true); // Just set to true to show preview section
          toast.success(`Successfully parsed ${results.data.length} orders`);
        } else {
          toast.error("No data found in CSV");
        }
      },
      error: (error) => {
        toast.error(`Error parsing CSV: ${error.message}`);
        console.error("CSV Parse Error:", error);
      },
    });
  };

  // Validate parsed data
  const validateData = () => {
    const errors = [];

    if (parsedData.length === 0) {
      errors.push("No data to process");
      return errors;
    }

    // Validate each row independently
    parsedData.forEach((row, index) => {
      const rowNumber = index + 1;
      const rowErrors = [];

      if (!row.symbol || row.symbol.trim() === "") {
        rowErrors.push("Symbol is required");
      }

      if (!row.strategy || row.strategy.trim() === "") {
        rowErrors.push("Strategy is required");
      }

      if (!row.qty || isNaN(parseInt(row.qty))) {
        rowErrors.push("Valid quantity is required");
      }

      if (!row.side || (row.side !== "1" && row.side !== "-1" && row.side !== 1 && row.side !== -1)) {
        rowErrors.push("Side must be 1 (Buy) or -1 (Sell)");
      }

      if (!row.orderType || !["MARKET", "LIMIT", "STOP", "STOP-LIMIT"].includes(row.orderType)) {
        rowErrors.push("Invalid order type");
      }

      if (!row.client_id || row.client_id.toString().trim() === "") {
        rowErrors.push("Missing client_id");
      }

      // If there are errors for this row, add them to the main errors array
      if (rowErrors.length > 0) {
        errors.push(`Row ${rowNumber}: ${rowErrors.join(", ")}`);
      }
    });

    return errors;
  };

  // Handle order placement
  const handleOrderPlacement = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error("Please upload a CSV file first");
      return;
    }

    setIsProcessing(true);

    // Validate data
    const validationErrors = validateData();
    if (validationErrors.length > 0) {
      toast.error("Validation errors found. Check console for details.");
      console.error("Validation Errors:", validationErrors);
      setIsProcessing(false);
      return;
    }

    // Send complete row-wise data
    const orderPayload = parsedData.map(row => ({
      symbol: row.symbol.trim(),
      strategy: row.strategy.trim(),
      qty: parseInt(row.qty),
      side: parseInt(row.side),
      orderType: row.orderType,
      stopPrice: parseFloat(row.stopPrice || 0),
      limitPrice: parseFloat(row.limitPrice || 0),
      client_id: row.client_id.toString().trim(),
    }));

    console.log("Order Payload:", orderPayload);
    console.log(`Placing orders for ${orderPayload.length} rows`);

    try {
      const response = await apiClient.post(
        "order-placement",
        orderPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Server response:", response.data);

      if (response.data.status === 200) {
        setOrderResults(response.data);
        setShowResultModal(true);
        toast.success(`Orders placed successfully for ${orderPayload.length} rows!`);
      } else {
        setOrderResults(response.data);
        setShowResultModal(true);
        toast.error(response.data.message || "Failed to place orders");
      }
    } catch (error) {
      console.error("Failed to place orders:", error);
      toast.error(`Error: ${error.message}`);
      setOrderResults({
        status: 500,
        message: error.message,
        data: []
      });
      setShowResultModal(true);
    } finally {
      setIsProcessing(false);
      console.log("Order placement process completed");
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = `client_id,symbol,strategy,qty,side,orderType,limitPrice,stopPrice,broker
CLIENT001,NSE:SBIN-EQ,HE_PC5_MA10,100,1,MARKET,0.0,0.0,fyers
CLIENT002,NSE:RELIANCE-EQ,HE_PC2,150,1,LIMIT,2450.5,0.0,fyers`;

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "order_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded successfully");
  };

  // Clear uploaded file
  const handleClearFile = () => {
    setCsvFile(null);
    setParsedData([]);
    setPreviewData(null);
  };

  // Close modal
  const closeModal = () => {
    setShowResultModal(false);
    setOrderResults(null);
  };

  return (
    <>
      <div className="flex flex-col gap-4 w-full mx-auto px-[30px] py-[20px]">
        <div>
          <p className="flex items-center text-[sm] font-semibold text-white">
            Fyers Bulk Order Placement (CSV Upload) 
            <span className="ml-2 text-sm text-gray-400">
              Upload a CSV with order details. Each row can have different symbols, strategies, and quantities.
            </span>
          </p>
        </div>

        <div className="flex flex-col gap-6 py-1 max-w-[1000px]">
          <div className="grid grid-cols-2 gap-6 py-1 max-w-[1000px]">
            {/* Download Template Section */}
            <div className="flex flex-col gap-3 p-4 border border-zinc-700 rounded-lg bg-zinc-900/50">
              <Label className="text-white font-semibold">Step 1: Download Template</Label>
              <p className="text-sm text-gray-400">
                Download the CSV template. Each row represents one order for one client.
              </p>
              <Button
                onClick={downloadTemplate}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Download CSV Template
              </Button>
            </div>

            {/* Upload Section */}
            <div className="flex flex-col gap-3 p-4 border border-zinc-700 rounded-lg bg-zinc-900/50">
              <Label className="text-white font-semibold">Step 2: Upload CSV File</Label>
              <p className="text-sm text-gray-400 mb-2">
                Required columns: client_id, symbol, strategy, qty, side, orderType
              </p>
              
              {!csvFile ? (
                <div className="relative h-[50px]">
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="h-[50px] text-white cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#07F2C7]/20 file:text-[#07F2C7] hover:file:bg-[#07F2C7]/30"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#07F2C7]" />
                    <span className="text-white text-sm">{csvFile.name}</span>
                    <span className="text-gray-400 text-xs">({parsedData.length} orders)</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFile}
                    className="text-red-400 hover:text-red-300 hover:bg-red-950/50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Preview Section - TABLE FORMAT */}
          {previewData && parsedData.length > 0 && (
            <div className="flex flex-col gap-3 p-4 border border-green-700/50 rounded-lg bg-green-950/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <Label className="text-white font-semibold">Order Preview</Label>
                <span className="text-gray-400 text-sm ml-2">({parsedData.length} orders)</span>
              </div>
                        {/* Action Buttons */}
              <div className="flex justify-end items-center space-x-4">
                <Button
                  className="w-[180px] text-[16px] bg-zinc-900 text-white font-poppins font-semibold hover:bg-zinc-800"
                  onClick={handleClearFile}
                  disabled={!csvFile || isProcessing}
                >
                  Clear
                </Button>
                <Button
                  className="w-[180px] text-[16px] bg-[#07F2C7]/80 text-black font-poppins font-semibold hover:bg-[#07F2C7] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleOrderPlacement}
                  disabled={!csvFile || isProcessing}
                >
                  {isProcessing ? "Processing..." : "Place Orders"}
                </Button>
              </div>
              </div>
              
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto rounded-lg border border-zinc-700">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-zinc-800 z-10">
                    <tr className="border-b border-zinc-700">
                      <th className="text-left p-2 text-xs font-semibold text-gray-300">Client ID</th>
                      <th className="text-left p-2 text-xs font-semibold text-gray-300">Symbol</th>
                      <th className="text-left p-2 text-xs font-semibold text-gray-300">Strategy</th>
                      <th className="text-right p-2 text-xs font-semibold text-gray-300">Qty</th>
                      <th className="text-center p-2 text-xs font-semibold text-gray-300">Side</th>
                      <th className="text-center p-2 text-xs font-semibold text-gray-300">Order Type</th>
                      <th className="text-right p-2 text-xs font-semibold text-gray-300">Limit Price</th>
                      <th className="text-right p-2 text-xs font-semibold text-gray-300">Stop Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-zinc-700/50 hover:bg-zinc-800/30 transition-colors"
                      >
                        <td className="p-2 text-xs text-white font-medium">
                          {row.client_id || "N/A"}
                        </td>
                        <td className="p-2 text-xs text-gray-300">
                          {row.symbol || "N/A"}
                        </td>
                        <td className="p-2 text-xs text-gray-300">
                          {row.strategy || "N/A"}
                        </td>
                        <td className="p-2 text-xs text-gray-300 text-right">
                          {row.qty || "N/A"}
                        </td>
                        <td className="p-2 text-xs text-center">
                          <span className={`inline-flex items-center justify-center w-14 px-2 py-0.5 rounded text-xs font-medium ${
                            row.side === 1 || row.side === "1" 
                              ? "bg-green-500/20 text-green-400" 
                              : "bg-red-500/20 text-red-400"
                          }`}>
                            {row.side === 1 || row.side === "1" ? "Buy" : "Sell"}
                          </span>
                        </td>
                        <td className="p-2 text-xs text-gray-300 text-center">
                          {row.orderType || "N/A"}
                        </td>
                        <td className="p-2 text-xs text-gray-300 text-right">
                          {row.limitPrice || row.limitPrice === 0 ? row.limitPrice : "N/A"}
                        </td>
                        <td className="p-2 text-xs text-gray-300 text-right">
                          {row.stopPrice || row.stopPrice === 0 ? row.stopPrice : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          
        </div>
      </div>

      {/* Results Modal */}
      {showResultModal && orderResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl w-[90%] max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header - COMPACT */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-zinc-800/50">
              <div className="flex items-center gap-2">
                {orderResults.status === 200 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <h2 className="text-base font-semibold text-white">Order Placement Results</h2>
                  <p className="text-xs text-gray-400">{orderResults.message}</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-zinc-700 rounded transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Content - COMPACT */}
            <div className="flex-1 overflow-y-auto p-4">
              {orderResults.summary && (
                <div className="mb-3 p-3 bg-zinc-800/80 rounded-lg border border-zinc-700">
                  <h3 className="text-xs font-semibold text-gray-300 mb-2">Summary</h3>
                  <div className="flex items-center gap-6 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-[#07F2C7] rounded-full"></div>
                      <span className="text-gray-400">Total Clients:</span>
                      <span className="text-white font-semibold">{orderResults.summary.total_clients}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-400">Total Orders:</span>
                      <span className="text-white font-semibold">{orderResults.summary.total_orders}</span>
                    </div>
                  </div>
                </div>
              )}

              {orderResults.data && orderResults.data.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-zinc-700">
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 bg-zinc-800 z-10">
                      <tr className="border-b border-zinc-700">
                        <th className="text-left p-2 text-xs font-semibold text-gray-300">Client ID</th>
                        <th className="text-left p-2 text-xs font-semibold text-gray-300">Order ID</th>
                        <th className="text-left p-2 text-xs font-semibold text-gray-300">Message</th>
                        <th className="text-center p-2 text-xs font-semibold text-gray-300">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderResults.data.map((client, clientIdx) => {
                        
                        const orders = client.order_resp || [];
                        return orders.map((order, orderIdx) => (
                             console.log("ORDER RES_-------------",order),
                          <tr
                            key={`${clientIdx}-${orderIdx}`}
                            className="border-b border-zinc-700/50 hover:bg-zinc-800/30 transition-colors"
                          >
                            {orderIdx === 0 && (
                              <td
                                className="p-2 text-xs text-white font-medium align-top"
                                rowSpan={orders.length}
                              >
                                <div className="flex items-center gap-1.5">
                                  <div className="w-1 h-1 bg-[#07F2C7] rounded-full"></div>
                                  {client.client_id}
                                </div>
                              </td>
                            )}
                            <td className="p-2 text-xs text-gray-300 font-mono">
                              {order?.response?.id || "N/A"}
                            </td>
                            <td className="p-2 text-xs text-gray-300">
                              {order?.response?.message || "No message"}
                            </td>
                            <td className="p-2 text-center">
                              {order?.status === "success" || order?.code === 1101 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                  <CheckCircle className="h-3 w-3" />
                                  Success
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-medium">
                                  <AlertCircle className="h-3 w-3" />
                                  Failed
                                </span>
                              )}
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <AlertCircle className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-xs">No order data available</p>
                </div>
              )}
            </div>

            {/* Modal Footer - COMPACT */}
            <div className="flex justify-end p-4 border-t border-zinc-700 bg-zinc-800/50">
              <Button
                onClick={closeModal}
                className="px-4 py-1.5 text-sm bg-[#07F2C7]/80 text-black font-semibold hover:bg-[#07F2C7] transition-all"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}



    </>
  );
};

export default FyersOrderPlacement;